import { chartColor } from '@/shared/lib/chart-theme';
import { formatCurrency } from '@/shared/lib/currency';
import {
  BarElement,
  CategoryScale,
  Chart,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type ChartData,
  type ChartDataset,
  type ChartOptions,
  type LegendOptions,
  type Plugin,
} from 'chart.js';
import { merge } from 'chart.js/helpers';
import type { FC, MouseEvent } from 'react';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
);

type LineOverlay = Omit<
  ChartDataset<'line', (number | null)[]>,
  'type' | 'data'
> & {
  data: (number | null)[];
};

interface BasicVerticalBarChartProps {
  data: ChartData<'bar'>;
  line?: LineOverlay;
  plugins?: Plugin<'bar'>[];
  legendProps?: Partial<LegendOptions<'bar'>>;
  options?: ChartOptions<'bar'>;
  onLabelClick?: (index: number, label: string) => void;
}

export const bringLineToFrontPlugin: Plugin<'bar' | 'line'> = {
  id: 'bringLineToFront',
  afterDatasetsDraw(chart) {
    const meta = chart
      .getSortedVisibleDatasetMetas()
      .find((m) => m.type === 'line');
    if (meta) {
      // redraw the line controller on foreground
      meta.controller.draw();
    }
  },
};

export const basicVerticalBarBackgroundPlugin = {
  id: 'barBackground',
  beforeDatasetsDraw: (chart: Chart<'bar'>) => {
    const {
      ctx,
      scales: { y },
    } = chart;

    chart.data.datasets.forEach((_, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.type !== 'bar') return;

      meta.data.forEach((bar) => {
        const barElement = bar as BarElement;
        const { x: barElementX, width: barElementWidth } = barElement.getProps(
          ['x', 'width'],
          true
        );
        if (!barElementX || !barElementWidth) return;

        const xPos = barElementX - barElementWidth / 2;
        const width = barElementWidth;
        const yZero = y.getPixelForValue(0);
        const yMax = y.getPixelForValue(y.max);

        ctx.save();
        ctx.fillStyle = chartColor('--color-chart-track');
        ctx.fillRect(xPos, yMax, width, yZero - yMax);
        ctx.restore();
      });
    });
  },
};

const BasicVerticalBarChart: FC<BasicVerticalBarChartProps> = ({
  data,
  line,
  plugins = [],
  legendProps = {},
  options = {},
  onLabelClick,
}) => {
  const base: ChartData<'bar' | 'line'> = {
    labels: data.labels,
    datasets: data.datasets.map((d) => ({ ...d, type: 'bar', order: 0, z: 0 })),
  };

  if (line?.data) {
    const lineDs: ChartDataset<'line', (number | null)[]> = {
      type: 'line',
      data: line.data,
      label: line.label ?? 'Line',
      yAxisID: line.yAxisID ?? 'y',
      pointRadius: line.pointRadius ?? 3,
      pointBackgroundColor: line.pointBackgroundColor ?? line.borderColor,
      pointBorderColor: line.pointBorderColor ?? line.borderColor,
      borderColor: line.borderColor,
      pointBorderWidth: line.pointBorderWidth ?? 2,
      order: line.order ?? 2,
    };
    base.datasets.push(lineDs);
  }

  const defaultOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: false, boxWidth: 8, boxHeight: 8 },
        ...legendProps,
      },
      tooltip: { enabled: true },
      datalabels: { display: false },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: { display: false },
        grid: {
          color: chartColor('--color-neutral-4'),
          tickBorderDash: [2, 5],
        },
        border: {
          dash: [2, 2],
          dashOffset: 1,
        },
      },
      y: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        ticks: { stepSize: 50 },
        title: { display: false },
        grid: {
          color: chartColor('--color-neutral-4'),
          tickBorderDash: [2, 5],
        },
        border: {
          display: false,
          dash: [2, 2],
          dashOffset: 1,
        },
      },
      y1: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        title: { display: false },
        border: { display: false },
        ticks: {
          display: data.datasets.length > 1,
          callback: (value) => formatCurrency(+value),
          stepSize: 50,
        },
      },
    },
  };

  const mergedOptions = merge(defaultOptions, options);

  const handleClick = (event: MouseEvent<HTMLCanvasElement>) => {
    if (!onLabelClick) return;

    const chart = ChartJS.getChart(event.currentTarget);
    if (!chart) return;

    const elements = chart.getElementsAtEventForMode(
      event.nativeEvent,
      'nearest',
      { intersect: true },
      false
    );

    if (elements.length > 0) {
      const index = elements[0].index;
      const label = chart.data.labels?.[index];
      if (typeof label === 'string') {
        onLabelClick(index, label);
      }
      return;
    }

    const xScale = chart.scales['x'];
    if (!xScale) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const chartArea = chart.chartArea;

    if (y < chartArea.bottom || y > xScale.bottom) return;

    const labels = chart.data.labels ?? [];
    for (let i = 0; i < labels.length; i++) {
      const labelX = xScale.getPixelForTick(i);
      const halfStep = xScale.width / labels.length / 2;

      if (x >= labelX - halfStep && x <= labelX + halfStep) {
        const label = labels[i];
        if (typeof label === 'string') {
          onLabelClick(i, label);
        }
        return;
      }
    }
  };

  return (
    <Bar
      plugins={plugins}
      options={mergedOptions}
      data={base as ChartData<'bar'>}
      style={onLabelClick ? { cursor: 'pointer' } : undefined}
      onClick={onLabelClick ? handleClick : undefined}
    />
  );
};
export { BasicVerticalBarChart };
